import { motion } from 'framer-motion';

export default function Speedometer({ speed = 0, maxSpeed = 80 }) {
    const clampedSpeed = Math.min(speed, maxSpeed);
    const percentage = clampedSpeed / maxSpeed;

    // Arc geometry: 240 degree sweep from -210 to 30 degrees
    const radius = 90;
    const strokeWidth = 8;
    const cx = 110;
    const cy = 110;
    const startAngle = -210;
    const endAngle = 30;
    const sweep = endAngle - startAngle; // 240 degrees

    const polarToCartesian = (angle) => {
        const rad = (angle * Math.PI) / 180;
        return {
            x: cx + radius * Math.cos(rad),
            y: cy + radius * Math.sin(rad),
        };
    };

    const describeArc = (startA, endA) => {
        const start = polarToCartesian(endA);
        const end = polarToCartesian(startA);
        const largeArc = endA - startA > 180 ? 1 : 0;
        return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`;
    };

    const activeAngle = startAngle + sweep * percentage;
    const needleEnd = polarToCartesian(activeAngle);

    // Speed color: blue -> cyan -> gold -> pink -> red
    const getSpeedColor = () => {
        if (percentage < 0.25) return '#1E90FF';
        if (percentage < 0.5) return '#00BFFF';
        if (percentage < 0.7) return '#FFD700';
        if (percentage < 0.85) return '#FF33CC';
        return '#FF3333';
    };

    const speedColor = getSpeedColor();

    // Tick marks
    const ticks = [];
    const tickCount = 8;
    for (let i = 0; i <= tickCount; i++) {
        const angle = startAngle + (sweep / tickCount) * i;
        const innerR = radius - 14;
        const outerR = radius - 4;
        const inner = {
            x: cx + innerR * Math.cos((angle * Math.PI) / 180),
            y: cy + innerR * Math.sin((angle * Math.PI) / 180),
        };
        const outer = {
            x: cx + outerR * Math.cos((angle * Math.PI) / 180),
            y: cy + outerR * Math.sin((angle * Math.PI) / 180),
        };
        const tickValue = Math.round((maxSpeed / tickCount) * i);
        const labelR = radius - 26;
        const label = {
            x: cx + labelR * Math.cos((angle * Math.PI) / 180),
            y: cy + labelR * Math.sin((angle * Math.PI) / 180),
        };

        ticks.push(
            <g key={i}>
                <line
                    x1={inner.x} y1={inner.y}
                    x2={outer.x} y2={outer.y}
                    stroke="rgba(255,255,255,0.3)"
                    strokeWidth={i % 2 === 0 ? 2 : 1}
                />
                {i % 2 === 0 && (
                    <text
                        x={label.x} y={label.y}
                        fill="rgba(255,255,255,0.5)"
                        fontSize="9"
                        fontFamily="var(--font-display)"
                        textAnchor="middle"
                        dominantBaseline="middle"
                    >
                        {tickValue}
                    </text>
                )}
            </g>
        );
    }

    return (
        <div className="relative" data-testid="speedometer">
            <svg width="220" height="180" viewBox="0 0 220 180">
                <defs>
                    <filter id="neonGlow">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <filter id="needleGlow">
                        <feGaussianBlur stdDeviation="2" result="blur" />
                        <feMerge>
                            <feMergeNode in="blur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                    <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#1E90FF" />
                        <stop offset="40%" stopColor="#00BFFF" />
                        <stop offset="65%" stopColor="#FFD700" />
                        <stop offset="85%" stopColor="#FF33CC" />
                        <stop offset="100%" stopColor="#FF3333" />
                    </linearGradient>
                </defs>

                {/* Background arc (track) */}
                <path
                    d={describeArc(startAngle, endAngle)}
                    fill="none"
                    stroke="rgba(255,255,255,0.06)"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                />

                {/* Active arc (filled) */}
                {percentage > 0.005 && (
                    <motion.path
                        d={describeArc(startAngle, activeAngle)}
                        fill="none"
                        stroke="url(#arcGradient)"
                        strokeWidth={strokeWidth}
                        strokeLinecap="round"
                        filter="url(#neonGlow)"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                )}

                {/* Tick marks */}
                {ticks}

                {/* Needle */}
                <motion.line
                    x1={cx}
                    y1={cy}
                    x2={needleEnd.x}
                    y2={needleEnd.y}
                    stroke={speedColor}
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    filter="url(#needleGlow)"
                    initial={false}
                    animate={{ x2: needleEnd.x, y2: needleEnd.y }}
                    transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                />

                {/* Center hub */}
                <circle cx={cx} cy={cy} r="6" fill={speedColor} opacity="0.8" filter="url(#needleGlow)" />
                <circle cx={cx} cy={cy} r="3" fill="white" opacity="0.9" />

                {/* Speed number */}
                <text
                    x={cx}
                    y={cy + 35}
                    fill="white"
                    fontSize="32"
                    fontFamily="var(--font-display)"
                    fontWeight="900"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{ filter: `drop-shadow(0 0 8px ${speedColor}80)` }}
                >
                    {Math.round(clampedSpeed)}
                </text>

                {/* Unit label */}
                <text
                    x={cx}
                    y={cy + 55}
                    fill="rgba(255,255,255,0.5)"
                    fontSize="10"
                    fontFamily="var(--font-display)"
                    fontWeight="600"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    letterSpacing="3"
                >
                    KM/H
                </text>
            </svg>
        </div>
    );
}
